import { useCallback, useEffect, useRef, useState } from 'react';

import type { DeployTriggerConfigRequest, ValidationErrorResponse } from '@/api/deploy-trigger';
import { getDeployTriggerConfig, saveDeployTriggerConfig } from '@/api/deploy-trigger';
import { ApiError } from '@/modules/api/api-client';

// --- Types ---

interface FieldErrors {
  targetProjectKey?: string;
  targetEnvironment?: string;
  artifactIdPattern?: string;
}

type NotificationType = 'success' | 'error';

interface Notification {
  type: NotificationType;
  message: string;
}

// --- Sub-components ---

function LoadingSkeleton() {
  return (
    <div className="animate-pulse space-y-4">
      <div className="h-5 w-32 rounded bg-gray-200 dark:bg-gray-700" />
      <div className="h-10 rounded-lg bg-gray-200 dark:bg-gray-700" />
      <div className="h-10 rounded-lg bg-gray-200 dark:bg-gray-700" />
      <div className="h-10 rounded-lg bg-gray-200 dark:bg-gray-700" />
    </div>
  );
}

function Toggle({
  checked,
  onChange,
  id,
  label,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  id: string;
  label: string;
}) {
  return (
    <label htmlFor={id} className="inline-flex items-center gap-2 cursor-pointer">
      <span className="text-sm font-medium text-content">{label}</span>
      <button
        id={id}
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={() => onChange(!checked)}
        className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-brand-600 focus:ring-offset-2 ${
          checked ? 'bg-brand-700' : 'bg-gray-300 dark:bg-gray-600'
        }`}
      >
        <span
          className={`inline-block h-3.5 w-3.5 rounded-full bg-white transition-transform ${
            checked ? 'translate-x-4.5' : 'translate-x-0.5'
          }`}
        />
      </button>
    </label>
  );
}

function StatusIndicator({ enabled }: { enabled: boolean }) {
  if (!enabled) return null;

  return (
    <span
      className="inline-flex items-center gap-1.5 text-xs font-medium"
      aria-label="Deployment trigger is active"
    >
      {/* Green indicator dot — #16a34a on white bg achieves >4.5:1 contrast ratio */}
      <span
        className="inline-block h-2.5 w-2.5 rounded-full"
        style={{ backgroundColor: '#16a34a' }}
        aria-hidden="true"
      />
      <span style={{ color: '#16a34a' }}>Active</span>
    </span>
  );
}

function FormField({
  id,
  label,
  value,
  onChange,
  placeholder,
  error,
  disabled,
  maxLength,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  error?: string;
  disabled?: boolean;
  maxLength?: number;
}) {
  const errorId = `${id}-error`;

  return (
    <div>
      <label htmlFor={id} className="block text-xs font-medium text-content-secondary mb-1">
        {label}
      </label>
      <input
        id={id}
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        maxLength={maxLength}
        aria-invalid={!!error}
        aria-describedby={error ? errorId : undefined}
        className={`w-full rounded-md border px-3 py-1.5 text-sm text-content bg-surface focus:outline-none focus:ring-2 focus:ring-brand-600 disabled:opacity-50 disabled:cursor-not-allowed ${
          error
            ? 'border-red-500 dark:border-red-400'
            : 'border-border'
        }`}
      />
      {error && (
        <p id={errorId} role="alert" className="mt-1 text-xs text-red-700 dark:text-red-400">
          {error}
        </p>
      )}
    </div>
  );
}

// --- Main Component ---

interface DeployTriggerConfigFormProps {
  repoName: string;
}

export function DeployTriggerConfigForm({ repoName }: DeployTriggerConfigFormProps) {
  // --- State ---
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [notification, setNotification] = useState<Notification | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});

  const [enabled, setEnabled] = useState(false);
  const [targetProjectKey, setTargetProjectKey] = useState('');
  const [targetEnvironment, setTargetEnvironment] = useState('');
  const [artifactIdPattern, setArtifactIdPattern] = useState('');

  const notificationTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // --- Notification auto-dismiss ---
  const showNotification = useCallback((type: NotificationType, message: string) => {
    if (notificationTimerRef.current) {
      clearTimeout(notificationTimerRef.current);
    }
    setNotification({ type, message });

    if (type === 'success') {
      notificationTimerRef.current = setTimeout(() => {
        setNotification(null);
        notificationTimerRef.current = null;
      }, 5000);
    }
  }, []);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (notificationTimerRef.current) {
        clearTimeout(notificationTimerRef.current);
      }
    };
  }, []);

  // --- Load config ---
  const loadConfig = useCallback(async () => {
    setLoading(true);
    setNotification(null);
    setFieldErrors({});
    try {
      const config = await getDeployTriggerConfig(repoName);
      setEnabled(config.enabled);
      setTargetProjectKey(config.targetProjectKey);
      setTargetEnvironment(config.targetEnvironment);
      setArtifactIdPattern(config.artifactIdPattern);
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        // No config exists — show default empty state
        setEnabled(false);
        setTargetProjectKey('');
        setTargetEnvironment('');
        setArtifactIdPattern('');
      } else {
        showNotification('error', 'Failed to load deployment trigger configuration. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }, [repoName, showNotification]);

  useEffect(() => {
    if (repoName) {
      loadConfig();
    }
  }, [repoName, loadConfig]);

  // --- Save config ---
  const handleSave = async () => {
    setSaving(true);
    setFieldErrors({});
    setNotification(null);

    const request: DeployTriggerConfigRequest = {
      enabled,
      targetProjectKey: targetProjectKey.trim(),
      targetEnvironment: targetEnvironment.trim(),
      artifactIdPattern: artifactIdPattern.trim(),
    };

    try {
      const saved = await saveDeployTriggerConfig(repoName, request);
      setEnabled(saved.enabled);
      setTargetProjectKey(saved.targetProjectKey);
      setTargetEnvironment(saved.targetEnvironment);
      setArtifactIdPattern(saved.artifactIdPattern);
      showNotification('success', 'Deployment trigger configuration saved successfully.');
    } catch (err) {
      if (err instanceof ApiError && err.status === 400) {
        // Field-level validation errors
        const body = err.body as ValidationErrorResponse | undefined;
        if (body?.fieldErrors) {
          setFieldErrors(body.fieldErrors);
        } else {
          showNotification('error', body?.message ?? 'Validation failed. Please check the form fields.');
        }
      } else {
        // 5xx or timeout
        showNotification('error', 'Failed to save configuration. Please try again.');
      }
    } finally {
      setSaving(false);
    }
  };

  // --- Render: Loading state ---
  if (loading) {
    return (
      <section className="mt-6">
        <h3 className="text-sm font-semibold text-content mb-3">Deployment Trigger</h3>
        <LoadingSkeleton />
      </section>
    );
  }

  // --- Render: Main form ---
  return (
    <section className="mt-6 space-y-4">
      {/* Header with toggle and status indicator */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h3 className="text-sm font-semibold text-content">Deployment Trigger</h3>
          <StatusIndicator enabled={enabled} />
        </div>
        <Toggle
          id="deploy-trigger-toggle"
          checked={enabled}
          onChange={setEnabled}
          label={enabled ? 'Enabled' : 'Disabled'}
        />
      </div>

      {/* Notification */}
      {notification && (
        <div
          role={notification.type === 'error' ? 'alert' : 'status'}
          className={`rounded-lg border p-3 ${
            notification.type === 'success'
              ? 'border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20'
              : 'border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20'
          }`}
        >
          <p
            className={`text-sm ${
              notification.type === 'success'
                ? 'text-green-700 dark:text-green-300'
                : 'text-red-700 dark:text-red-300'
            }`}
          >
            {notification.message}
          </p>
        </div>
      )}

      {/* Form fields */}
      <div className="space-y-3">
        <FormField
          id="deploy-trigger-project-key"
          label="Target Project Key"
          value={targetProjectKey}
          onChange={(v) => {
            setTargetProjectKey(v);
            if (fieldErrors.targetProjectKey) {
              setFieldErrors((prev) => ({ ...prev, targetProjectKey: undefined }));
            }
          }}
          placeholder="CRVS-svc-ci"
          error={fieldErrors.targetProjectKey}
          disabled={saving}
          maxLength={128}
        />

        <FormField
          id="deploy-trigger-environment"
          label="Target Environment"
          value={targetEnvironment}
          onChange={(v) => {
            setTargetEnvironment(v);
            if (fieldErrors.targetEnvironment) {
              setFieldErrors((prev) => ({ ...prev, targetEnvironment: undefined }));
            }
          }}
          placeholder="staging"
          error={fieldErrors.targetEnvironment}
          disabled={saving}
          maxLength={64}
        />

        <FormField
          id="deploy-trigger-artifact-pattern"
          label="Artifact ID Pattern"
          value={artifactIdPattern}
          onChange={(v) => {
            setArtifactIdPattern(v);
            if (fieldErrors.artifactIdPattern) {
              setFieldErrors((prev) => ({ ...prev, artifactIdPattern: undefined }));
            }
          }}
          placeholder="CRVS-svc-ci"
          error={fieldErrors.artifactIdPattern}
          disabled={saving}
          maxLength={256}
        />
      </div>

      {/* Save button */}
      <div className="pt-2">
        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          className="inline-flex items-center gap-2 rounded-md bg-brand-700 px-4 py-2 text-sm font-medium text-white hover:bg-brand-800 focus:outline-none focus:ring-2 focus:ring-brand-600 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {saving && (
            <svg
              className="h-4 w-4 animate-spin"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
          )}
          {saving ? 'Saving...' : 'Save'}
        </button>
      </div>
    </section>
  );
}
